import React, { useContext, useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import { productCategories, siteConfig } from "../Datas/productData";
import "../css/header.scss";

function Header() {
  const { cartCount, wishlistCount } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.classList.toggle("nav-is-open", menuOpen);
    return () => document.body.classList.remove("nav-is-open");
  }, [menuOpen]);

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : "/shop");
  };

  const navClassName = ({ isActive }) =>
    `site-header__nav-link${isActive ? " is-active" : ""}`;
  const activeShopView = new URLSearchParams(location.search);
  const isNewView = location.pathname === "/shop" && activeShopView.get("new") === "true";
  const isSaleView = location.pathname === "/shop" && activeShopView.get("sale") === "true";
  const shopNavClassName = ({ isActive }) =>
    `site-header__nav-link${isActive && !isNewView && !isSaleView ? " is-active" : ""}`;

  return (
    <header className="site-header">
      <div className="site-header__announcement">
        <Link to="/shipping">
          Free shipping &amp; returns in the U.S. on orders ${siteConfig.freeShippingThreshold}+
        </Link>
      </div>

      <div className="site-header__bar">
        <button
          className="site-header__mobile-button"
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Open navigation"
          aria-expanded={menuOpen}
        >
          <FiMenu aria-hidden="true" />
        </button>

        <Link className="site-header__logo" to="/" aria-label={`${siteConfig.brandName} home`}>
          {siteConfig.logo.text}
        </Link>

        <nav className="site-header__desktop-nav" aria-label="Main navigation">
          <NavLink className={navClassName} to="/" end>
            Home
          </NavLink>
          <div className="site-header__shop-nav">
            <NavLink className={shopNavClassName} to="/shop">
              Shop <FiChevronDown aria-hidden="true" />
            </NavLink>
            <div className="site-header__mega-menu">
              <div>
                <span>Shop denim</span>
                <Link to="/shop?category=Jeans">Raw selvedge denim</Link>
                <Link to="/shop?category=Jeans&fit=Straight">Straight leg fits</Link>
                <Link to="/shop?category=Jeans&fit=Tapered">Tapered fits</Link>
                <Link to="/shop?category=Jeans&fit=Wide">Wide leg fits</Link>
              </div>
              <div>
                <span>Workwear</span>
                <Link to="/shop?category=Pants">Pants</Link>
                <Link to="/shop?category=Tops">Shirts &amp; tops</Link>
                <Link to="/shop?category=Jackets">Jackets</Link>
                <Link to="/shop?category=Accessories">Accessories</Link>
              </div>
              <div className="site-header__mega-feature">
                <p>Made to wear in, not wear out.</p>
                <Link to="/shop">Shop our collection →</Link>
              </div>
            </div>
          </div>
          <Link
            className={`site-header__nav-link${isNewView ? " is-active" : ""}`}
            to="/shop?new=true"
            aria-current={isNewView ? "page" : undefined}
          >
            New arrivals
          </Link>
          <NavLink className={navClassName} to="/about">
            About
          </NavLink>
          <Link
            className={`site-header__nav-link${isSaleView ? " is-active" : ""}`}
            to="/shop?sale=true"
            aria-current={isSaleView ? "page" : undefined}
          >
            Sale
          </Link>
        </nav>

        <div className="site-header__tools">
          <button
            type="button"
            onClick={() => setSearchOpen((current) => !current)}
            aria-label="Search"
            aria-expanded={searchOpen}
          >
            <FiSearch aria-hidden="true" />
          </button>
          <Link className="site-header__account" to="/contact" aria-label="Customer care">
            <FiUser aria-hidden="true" />
          </Link>
          <Link className="site-header__count-link" to="/shop?wishlist=true" aria-label={`Wishlist with ${wishlistCount} items`}>
            <FiHeart aria-hidden="true" />
            {wishlistCount > 0 && <span>{wishlistCount}</span>}
          </Link>
          <Link className="site-header__count-link" to="/cartpage" aria-label={`Cart with ${cartCount} items`}>
            <FiShoppingBag aria-hidden="true" />
            {cartCount > 0 && <span>{cartCount}</span>}
          </Link>
        </div>
      </div>

      <div className={`site-header__search${searchOpen ? " is-open" : ""}`}>
        <form onSubmit={handleSearch} role="search">
          <FiSearch aria-hidden="true" />
          <label className="sr-only" htmlFor="site-search">
            Search products
          </label>
          <input
            id="site-search"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search jeans, jackets and workwear"
          />
          <button type="submit">Search</button>
        </form>
      </div>

      <div
        className={`site-header__drawer-overlay${menuOpen ? " is-open" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden={!menuOpen}
      />
      <aside className={`site-header__drawer${menuOpen ? " is-open" : ""}`} aria-hidden={!menuOpen}>
        <div className="site-header__drawer-head">
          <span>{siteConfig.logo.text}</span>
          <button type="button" onClick={() => setMenuOpen(false)} aria-label="Close navigation">
            <FiX aria-hidden="true" />
          </button>
        </div>
        <nav aria-label="Mobile navigation">
          <NavLink to="/">Home</NavLink>
          <NavLink to="/shop">Shop all</NavLink>
          <div className="site-header__drawer-categories">
            {productCategories.slice(1).map((category) => (
              <Link key={category} to={`/shop?category=${encodeURIComponent(category)}`}>
                {category}
              </Link>
            ))}
          </div>
          <NavLink to="/shop?new=true">New arrivals</NavLink>
          <NavLink to="/about">About</NavLink>
          <NavLink to="/contact">Contact</NavLink>
        </nav>
        <div className="site-header__drawer-foot">
          <Link to="/shop?wishlist=true"><FiHeart aria-hidden="true" /> Wishlist ({wishlistCount})</Link>
          <Link to="/cartpage"><FiShoppingBag aria-hidden="true" /> Cart ({cartCount})</Link>
        </div>
      </aside>
    </header>
  );
}

export default Header;
